import { motion } from "framer-motion";
import { Activity, Shield, Terminal, Workflow } from "lucide-react";
import { Reveal, SectionHeading } from "./Reveal";

const SERVICES = [
  {
    id: "consulting",
    icon: Shield,
    badge: "Strategy & Governance",
    title: "Cybersecurity Consulting",
    desc: "Strategic risk management, architecture review, Zero Trust roadmap design, and vCISO advisory for cloud & hybrid environments.",
    span: "lg:col-span-2",
  },
  {
    id: "secops",
    icon: Activity,
    badge: "Operations",
    title: "Security Operations & Process Optimization",
    desc: "Eliminate SOC friction and alert noise with process redesign, SLA optimization, and analyst workflow harmonization.",
    span: "lg:col-span-1",
  },
  {
    id: "use-case-dev",
    icon: Terminal,
    badge: "Engineering",
    title: "Use Case Development & Engineering",
    desc: "Custom detection rules mapped to MITRE ATT&CK TTPs — SIEM/XDR rule engineering for Splunk, Sentinel, and Elastic.",
    span: "lg:col-span-1",
  },
  {
    id: "playbook-design",
    icon: Workflow,
    badge: "Automation",
    title: "Custom Playbook Design & SOAR",
    desc: "End-to-end incident response playbook creation and automation. Standardize triage, containment, and eradication protocols.",
    span: "lg:col-span-2",
    pipeline: true,
  },
];

const PIPELINE = ["Detect", "Triage", "Contain", "Recover"];

export default function Services() {
  return (
    <section
      id="services"
      data-testid="services-section"
      className="relative py-20 sm:py-28 lg:py-32"
    >
      <div className="pointer-events-none absolute left-1/2 top-0 h-[420px] w-[720px] -translate-x-1/2 rounded-full bg-cyan-500/6 blur-[140px]" />
      <div className="container relative">
        <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
          <SectionHeading
            index="02"
            label="What we do"
            title="Services built for the modern threat surface"
          />
          <Reveal delay={0.15}>
            <p className="max-w-sm text-sm leading-relaxed text-slate-400">
              From boardroom strategy to the analyst console — four disciplines,
              one coherent defense.
            </p>
          </Reveal>
        </div>

        <div className="mt-14 grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3 lg:gap-6">
          {SERVICES.map((s, i) => (
            <Reveal
              key={s.id}
              delay={0.08 + i * 0.07}
              className={s.span}
            >
              <article
                data-testid={`service-card-${s.id}`}
                className={`group relative flex h-full flex-col overflow-hidden rounded-2xl border border-white/8 bg-panel/60 p-6 transition-all duration-300 hover:-translate-y-1 hover:border-cyan-300/35 hover:bg-panelhi/50 hover:shadow-[0_24px_70px_rgba(0,240,255,0.09)] sm:p-8 ${
                  s.span ? `md:col-span-2 lg:col-span-2` : ""
                }`}
              >
                <div className="pointer-events-none absolute -right-16 -top-16 h-44 w-44 rounded-full bg-cyan-400/5 blur-3xl transition-opacity duration-500 group-hover:bg-cyan-400/12" />

                <div className="flex items-start justify-between gap-4">
                  <div className="flex h-11 w-11 items-center justify-center rounded-xl border border-cyan-300/20 bg-cyan-400/8 text-cyan-300 transition-colors duration-300 group-hover:border-emerald-300/30 group-hover:text-emerald-300">
                    <s.icon className="h-5 w-5" />
                  </div>
                  <span className="rounded-full border border-white/10 bg-white/[0.03] px-3 py-1 font-mono text-[9px] uppercase tracking-[0.24em] text-slate-400">
                    {s.badge}
                  </span>
                </div>

                <h3 className="mt-6 font-display text-lg font-bold leading-snug text-slate-50 sm:text-xl">
                  {s.title}
                </h3>
                <p className="mt-3 flex-1 text-sm leading-relaxed text-slate-400">
                  {s.desc}
                </p>

                {s.pipeline && (
                  <div className="mt-7 flex flex-wrap items-center gap-y-2 font-mono text-[10px] uppercase tracking-[0.18em] text-slate-400">
                    {PIPELINE.map((step, j) => (
                      <div key={step} className="flex items-center">
                        <div className="flex items-center gap-1.5 rounded-full border border-white/10 bg-white/[0.04] px-2.5 py-1">
                          <motion.span
                            className="h-1.5 w-1.5 rounded-full bg-emerald-400"
                            animate={{ opacity: [0.3, 1, 0.3] }}
                            transition={{
                              duration: 1.6,
                              repeat: Infinity,
                              delay: j * 0.4,
                            }}
                          />
                          {step}
                        </div>
                        {j < PIPELINE.length - 1 && (
                          <div className="hidden h-px w-5 bg-gradient-to-r from-cyan-400/50 to-emerald-400/30 sm:block" />
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </article>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
